import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function InviteMemberDialog({
  open,
  onOpenChange,
  inviteEmail,
  onInviteEmailChange,
  inviteRole,
  onInviteRoleChange,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inviteEmail: string;
  onInviteEmailChange: (email: string) => void;
  inviteRole: "owner" | "member";
  onInviteRoleChange: (role: "owner" | "member") => void;
  onSubmit: (e: React.FormEvent) => void;
  isPending: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md overflow-x-hidden">
        <form onSubmit={onSubmit} className="min-w-0">
          <DialogHeader>
            <DialogTitle>Invite Teammate to Workspace</DialogTitle>
            <DialogDescription>
              Teammates get access to all projects in this workspace. To invite
              someone to a single project only, invite them from that
              Project&apos;s settings.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 min-w-0">
            <div className="space-y-2 min-w-0">
              <Label htmlFor="invite-email">Email address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@example.com"
                value={inviteEmail}
                onChange={(e) => onInviteEmailChange(e.target.value)}
                autoFocus
                required
              />
            </div>

            <div className="space-y-2 min-w-0">
              <Label htmlFor="invite-role">Workspace Role</Label>
              <Select
                value={inviteRole}
                onValueChange={(v) =>
                  onInviteRoleChange(v as "owner" | "member")
                }
              >
                <SelectTrigger
                  id="invite-role"
                  className="w-full max-w-full min-w-0 overflow-hidden"
                >
                  <SelectValue>
                    <span className="truncate">
                      {inviteRole === "owner"
                        ? "Owner — Full admin access"
                        : "Member — Editor access across projects"}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  className="z-[100] w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]"
                >
                  <SelectItem value="member" className="items-start py-2">
                    <div className="min-w-0 whitespace-normal text-left">
                      <div className="font-medium">Member</div>
                      <div className="text-xs text-muted-foreground whitespace-normal">
                        Editor access across all workspace projects; cannot
                        manage workspace settings.
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="owner" className="items-start py-2">
                    <div className="min-w-0 whitespace-normal text-left">
                      <div className="font-medium">Owner</div>
                      <div className="text-xs text-muted-foreground whitespace-normal">
                        Full admin access to all projects, billing, and team
                        members.
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!inviteEmail.trim() || isPending}>
              {isPending ? "Inviting..." : "Create Invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RemoveMemberDialog({
  member,
  onClose,
  onConfirm,
  isPending,
}: {
  member: {
    userId: string;
    displayName: string | null;
    isSelf: boolean;
  } | null;
  onClose: () => void;
  onConfirm: (userId: string) => void;
  isPending: boolean;
}) {
  return (
    <Dialog open={!!member} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {member?.isSelf ? "Leave Workspace" : "Remove Team Member"}
          </DialogTitle>
          <DialogDescription>
            {member?.isSelf
              ? "Are you sure you want to leave this workspace? You will lose access to all its projects."
              : `Are you sure you want to remove ${member?.displayName || "this user"} from the workspace? They will lose access to all projects immediately.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              if (member) onConfirm(member.userId);
            }}
            disabled={isPending}
          >
            {isPending ? "Removing..." : member?.isSelf ? "Leave" : "Remove"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TransferOwnershipDialog({
  member,
  onClose,
  onConfirm,
  isPending,
}: {
  member: {
    userId: string;
    displayName: string | null;
  } | null;
  onClose: () => void;
  onConfirm: (userId: string) => void;
  isPending: boolean;
}) {
  return (
    <Dialog open={!!member} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Transfer Workspace Ownership</DialogTitle>
          <DialogDescription>
            Are you sure you want to transfer ownership of this workspace to{" "}
            <strong className="text-foreground">
              {member?.displayName || "this member"}
            </strong>
            ?
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 text-xs text-muted-foreground">
          You will become a regular member of the workspace. Only the new owner
          will be able to manage billing, rename or delete the workspace, and
          transfer ownership.
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={isPending}
            onClick={() => {
              if (member) onConfirm(member.userId);
            }}
          >
            {isPending ? "Transferring..." : "Transfer Ownership"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
